import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Button, Eyebrow, FaqList, Heading, MarginNote, Reveal, SiteImage, SiteSection, TextLink,
} from '@/components/site';
import { EnquiryButton } from '@/components/site/enquiry';
import { DemoOfferButton } from '@/components/site/demo';
import { PlansFromLine, PricingTeaser } from '@/components/site/pricing';
import { FREE_LISTING_CLOSED_NOTE, useFreeListingState } from '@/lib/inventory';
import { ReportPhoto } from './audienceParts';

/**
 * The homepage's page-specific parts (Sept 26 2026 design, `site/index.html` +
 * `site/style.css` + the home parts of `site/app.js`), composed by HomeSlot.jsx.
 * Tokens only (no hex/rgb/hsl, no inline style objects); headings never bold.
 *
 * Content width: the site's content cap (1680px + the page gutter on each side; owner
 * 2026-09-28), like every other page.
 */
export const HOME_WIDTH = 'max-w-canvas';

const DESKTOP_QUERY = '(min-width: 800px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** `matchMedia(query).matches`, kept current; false where matchMedia does not exist. */
export function useMediaQuery(query) {
  const get = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
      return window.matchMedia(query).matches;
    } catch (e) {
      return false;
    }
  };
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch (e) {
      return undefined;
    }
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
      else if (typeof mql.removeListener === 'function') mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

/** The logo the site header already loads (same URL, so the overlays cost nothing). */
const LOGO_SRC = '/tappd-logo.png';

/*
 * DemoOfferButton (components/site/demo) renders a site Button; where the example offer (or
 * the enquiry dialog) opens from a text link, it is restyled:
 * - DEMO_TEXT_LINK: "Try an example offer →" drawn like TextLink (underlined DM Sans
 *   semibold, rust on hover). `shadow-transparent` also makes cn() drop Button's
 *   shadow-site classes.
 */
const DEMO_TEXT_LINK = 'min-h-0 gap-0 border-0 border-b border-current bg-transparent px-0 py-[7px] text-sm leading-normal shadow-transparent hover:bg-transparent hover:text-brand-rust hover:shadow-transparent nav:min-h-0 nav:text-[0.9375rem] motion-safe:hover:translate-y-0';

// ---------------------------------------------------------------------------
// Hero (upgrade brief §3.1 item 1): text left, the photo right from 800px; on a phone the
// photo follows the actions (4:5). No reveal: everything is visible at first paint.
// ---------------------------------------------------------------------------

/** Rendered width of the hero photo: the right column from 800px, the page width below. */
export const HERO_SIZES = '(min-width: 1808px) 1008px, (min-width: 800px) 50vw, 100vw';

/** "See a coaster in action": scrolls to the first story chapter (brief §3.1 hero copy). */
function toFirstChapter(e) {
  const el = typeof document !== 'undefined' ? document.getElementById('how-it-works') : null;
  if (!el || typeof el.scrollIntoView !== 'function') return;
  e.preventDefault();
  const reduced = typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  if (window.history && typeof window.history.replaceState === 'function') window.history.replaceState(null, '', '#how-it-works');
}

export function HomeHero() {
  return (
    <section
      id="top"
      aria-labelledby="home-hero-title"
      className="site-light px-gutter pb-[34px] pt-[112px] text-navy nav:pb-[50px] nav:pt-[132px]"
    >
      <div className={cn('mx-auto grid grid-cols-1 items-center gap-8 nav:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] nav:gap-10 min-[1151px]:gap-14', 'max-w-[1680px]')}>
        <div className="min-w-0">
          <Eyebrow
            as="p"
            tone="muted"
            className="mb-5 text-[11px] leading-[1.65] tracking-[0.05em] nav:mb-6 nav:text-xs nav:tracking-[0.07em]"
          >
            Coaster advertising · South Jersey
          </Eyebrow>
          <Heading
            level={1}
            size="display"
            id="home-hero-title"
            // Two lines in the left column from 800px (brief §3.1): every breakpoint of the
            // display size restated, the leading after the sizes (tailwind-merge).
            className="text-[clamp(2.625rem,11vw,3.5rem)] sm:text-[3.5rem] nav:text-[clamp(3rem,4.6vw,4.375rem)] xl:text-[clamp(3rem,4.6vw,4.375rem)] leading-[1.06] nav:leading-[1.04]"
          >
            Local discovery. <br />
            <em>Right on the <span className="site-underline">table.</span></em>
          </Heading>
          <p className="mb-6 mt-5 max-w-[34rem] text-base leading-[1.6] text-brand-muted nav:mb-7 nav:mt-6 nav:text-[1.0625rem]">
            Local businesses put their offer on the coasters at neighborhood bars and restaurants. Guests scan and
            claim it. Venues get the coasters free.
          </p>
          <div className="grid max-w-[330px] grid-cols-1 gap-2.5 min-[431px]:flex min-[431px]:max-w-none min-[431px]:flex-wrap nav:gap-3.5">
            <Button to="/for-businesses">Advertise your business</Button>
            <Button to="/for-venues" variant="secondary">Get free coasters</Button>
          </div>
          <Link
            to="/local-deals-near-me"
            className="inline-block pt-[18px] text-sm leading-[1.7] text-brand-muted nav:leading-normal"
          >
            Just here for the deals?{' '}
            <span className="text-navy underline underline-offset-4">
              Find something nearby<span aria-hidden="true">{' →'}</span>
            </span>
          </Link>
          <MarginNote
            tone="rust"
            tilt={-4}
            className="mt-6 hidden text-[1.5rem] leading-[1.05] nav:block"
          >
            small coaster. big local energy.
          </MarginNote>
        </div>
        <div className="relative min-w-0">
          <SiteImage
            id="HOME-HERO-01"
            ratio="4x5"
            navRatio="16x9"
            navBox="4x3"
            priority
            sizes={HERO_SIZES}
            className="border border-navy/15 shadow-[8px_9px_0_theme(colors.navy.DEFAULT/12%)]"
            testId="home-hero-photo"
          />
          <p className="absolute left-3 top-3 m-0 bg-navy px-[13px] py-[9px] font-label text-[10px] uppercase tracking-[0.08em] text-brand-sheet nav:left-4 nav:top-4 nav:text-[11px]">
            Sip. Scan. Save.
          </p>
          <a
            href="#how-it-works"
            onClick={toFirstChapter}
            className="absolute bottom-3 right-3 inline-flex min-h-11 items-center gap-3 border border-brand-sheet/50 bg-brand-sheet/90 px-[14px] py-[11px] font-label text-xs text-navy no-underline hover:bg-brand-sheet nav:bottom-4 nav:right-4"
          >
            See a coaster in action
            <span aria-hidden="true" className="font-body text-lg leading-none">↓</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Get started (#choose, the header's "Get started"): three photo cards (brief §3.1 item 2).
// ---------------------------------------------------------------------------
export const FIRST_ROUTES = [
  {
    eyebrow: 'Local businesses',
    title: 'A seat at the table.',
    text: 'Put your offer on the coasters at neighborhood bars and restaurants.',
    to: '/for-businesses',
    photo: 'HOME-AUD-01',
  },
  {
    eyebrow: 'Bars & restaurants',
    title: 'Your next round is on us.',
    text: 'Free custom coasters with your name, paid for by local advertisers.',
    to: '/for-venues',
    photo: 'HOME-AUD-02',
  },
  {
    eyebrow: 'Guests',
    title: 'Just here for the deals?',
    text: 'Pick a venue and see the local offers around its tables.',
    to: '/local-deals-near-me',
    photo: 'HOME-AUD-03',
  },
];

export function FirstRoutes() {
  return (
    <section
      id="choose"
      aria-labelledby="home-choose-title"
      className="site-light scroll-mt-[110px] px-gutter text-navy"
    >
      <h2 id="home-choose-title" className="sr-only">Get started</h2>
      {/* Phones: compact rows (a square photo left); from 800px: three cards, the photo on top (4:3). */}
      <ul className={cn('m-0 mx-auto grid list-none grid-cols-1 gap-3 p-0 nav:grid-cols-3 nav:gap-5', 'max-w-[1680px]')}>
        {FIRST_ROUTES.map((r, i) => (
          <li key={r.to} className="min-w-0">
            <Link
              to={r.to}
              className="group/route flex h-full flex-row border border-brand-rule bg-brand-sheet no-underline transition-colors duration-180 ease-out hover:border-navy/40 nav:flex-col"
            >
              <SiteImage
                id={r.photo}
                ratio="4x3"
                box="1x1"
                navBox="4x3"
                decorative
                sizes="(min-width: 1808px) 547px, (min-width: 800px) 32vw, 112px"
                className="w-[112px] shrink-0 nav:w-full"
              />
              <span className="flex min-w-0 flex-1 flex-col justify-center px-4 py-3 nav:justify-start nav:px-7 nav:py-6">
                <Eyebrow index={i + 1} tone="muted" className="mb-1 flex text-[11px] leading-[1.65] nav:mb-[9px] nav:text-xs nav:leading-[1.65]">
                  {r.eyebrow}
                </Eyebrow>
                <span className="flex items-center justify-between gap-3 font-display text-[1.3125rem] font-medium leading-[1.15] tracking-[-0.03em] nav:gap-5 nav:text-[1.6875rem] nav:leading-[1.2]">
                  {r.title}
                  <span aria-hidden="true" className="font-body text-[1.5rem] font-normal leading-none transition-transform duration-180 motion-safe:group-hover/route:translate-x-1">→</span>
                </span>
                <span className="mt-1.5 hidden text-[0.9375rem] leading-[1.5] text-brand-muted min-[400px]:block nav:mt-2">{r.text}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// "It starts with a little curiosity." (#how-it-works): three chapters + the stage.
// ---------------------------------------------------------------------------
export const STORY_STEPS = [
  {
    chapter: 'On the table',
    short: 'Table',
    photo: 'HOME-CH-01',
    title: ['Your business.', 'In good company.'],
    text: 'A spot on a coaster at a neighborhood bar or restaurant. Your offer, right where local people are already spending time.',
  },
  {
    chapter: 'On their phone',
    short: 'Phone',
    photo: 'HOME-CH-02',
    title: ['One scan.', 'A new local find.'],
    text: 'Guests scan to browse offers from businesses nearby. No app to download. No account needed to take a look.',
  },
  {
    chapter: 'Around the corner',
    short: 'Visit',
    photo: 'HOME-CH-03',
    title: ['A reason to visit.', 'A way to measure.'],
    text: 'Guests claim with an email and get a code to show at the business. You see recorded activity, from scans to claims and redemptions.',
  },
];

/** The sample report rows on the third stage (structure only, no results). */
export const STORY_REPORT_ROWS = [
  ['Coasters placed', 'Placement records'],
  ['QR scans', 'Recorded activity'],
  ['Offer claims', 'Claim codes'],
  ['Redemptions', 'Recorded uses'],
];

const STAGE_ID = 'home-story-stage';

function stepAction(i) {
  if (i === 0) {
    return (
      <EnquiryButton role="advertiser" variant="secondary" arrow={false} className={DEMO_TEXT_LINK}>
        Check availability<span aria-hidden="true">{' →'}</span>
      </EnquiryButton>
    );
  }
  if (i === 1) {
    return (
      <DemoOfferButton variant="secondary" arrow={false} className={DEMO_TEXT_LINK}>
        Try an example offer<span aria-hidden="true">{' →'}</span>
      </DemoOfferButton>
    );
  }
  return <TextLink to="#reporting" arrow>See what gets counted</TextLink>;
}

/** Shared state of an overlay card on the stage: shown (current) or faded out, inert and hidden. */
function overlayProps(current, i) {
  const on = current === i;
  return {
    'aria-hidden': on ? undefined : 'true',
    inert: on ? undefined : '',
    'data-active': on ? 'true' : 'false',
  };
}

const OVERLAY = 'absolute z-[2] motion-safe:transition-[opacity,transform] motion-safe:duration-500 motion-safe:ease-out';
const OVERLAY_OFF = 'pointer-events-none translate-y-[30px] rotate-3 opacity-0';

function StoryStage({ stage, onGo, stageRef }) {
  return (
    <div
      ref={stageRef}
      id={STAGE_ID}
      data-stage={stage}
      className="relative hidden h-[535px] overflow-hidden border border-brand-rule bg-brand-rule nav:block xl:h-[540px] nav:motion-safe:sticky nav:motion-safe:top-[140px]"
    >
      {/* Each chapter has its own photo (brief §3.1 item 3); the current one shows. The
          chapter text beside the stage says what each photo shows, so they are decorative. */}
      {STORY_STEPS.map((s, i) => (
        <SiteImage
          key={s.photo}
          id={s.photo}
          ratio="1x1"
          fill
          decorative
          tone="paper"
          sizes="(min-width: 1808px) 979px, 58vw"
          className={cn(
            'motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-out',
            stage === i ? 'opacity-100' : 'opacity-0',
          )}
        />
      ))}

      {/* 01: the coaster note */}
      <div
        {...overlayProps(stage, 0)}
        className={cn(
          OVERLAY,
          'bottom-[77px] left-[14px] max-w-[90%] bg-brand-sheet p-[18px] shadow-[4px_5px_0_theme(colors.navy.DEFAULT/19%)] min-[360px]:left-[23px] min-[360px]:px-6 min-[360px]:py-5',
          stage === 0 ? '-rotate-3 opacity-100' : OVERLAY_OFF,
        )}
      >
        <Eyebrow className="text-[10px] leading-[1.65]">A little local discovery</Eyebrow>
        <p className="mb-5 mt-[13px] font-display text-2xl font-medium leading-[1.1] tracking-[-0.02em] min-[360px]:text-[1.6875rem]">
          The next good thing <br />
          is closer than you think.
        </p>
        <span className="font-label text-[10px] uppercase text-brand-rust">Sip. Scan. Save.</span>
      </div>

      {/* 02: an example offer on a phone-sized card */}
      <div
        {...overlayProps(stage, 1)}
        className={cn(
          OVERLAY,
          'left-1/2 top-5 -ml-[126px] w-[252px] border border-navy bg-brand-sheet p-[17px] shadow-[8px_10px_0_theme(colors.navy.DEFAULT/12%)] min-[360px]:-ml-[155px] min-[360px]:w-[310px] min-[360px]:p-5',
          stage === 1 ? 'opacity-100' : OVERLAY_OFF,
        )}
      >
        <div className="mb-[15px] flex items-center justify-between border-b border-brand-rule pb-3">
          <img src={LOGO_SRC} alt="Tappd" width={3290} height={1111} loading="lazy" className="h-auto w-[77px]" />
          <span className="font-label text-[9px] uppercase text-brand-muted">Example</span>
        </div>
        <Eyebrow className="text-[10px] leading-[1.65]">Around your table</Eyebrow>
        <Heading level={4} size="minor" className="mb-[13px] mt-2 text-[1.8125rem] leading-[1.12] min-[360px]:text-[1.9375rem]">
          Good things. <br />
          Close by.
        </Heading>
        <div className="mb-3.5 bg-brand-orange p-3.5 text-navy min-[360px]:p-4">
          <span className="font-label text-[9px] uppercase">Your next local find</span>
          <strong className="my-3 block font-display text-[1.625rem] font-semibold leading-[1.05] tracking-[-0.02em] min-[360px]:text-[1.8125rem]">
            10% off <br />
            your next ride.
          </strong>
          <span className="text-[10px] min-[360px]:text-[11px]">At your neighborhood bike shop</span>
        </div>
        <DemoOfferButton size="sm" fullWidth className="min-h-10">
          Explore this offer
        </DemoOfferButton>
        <span className="mt-2.5 block text-[10px] text-brand-muted">Illustrative offer · not redeemable</span>
      </div>

      {/* 03: what a report covers (structure only) */}
      <div
        {...overlayProps(stage, 2)}
        className={cn(
          OVERLAY,
          'inset-x-3.5 top-[65px] bg-brand-sheet p-5 shadow-[7px_7px_0_theme(colors.navy.DEFAULT/19%)] min-[360px]:inset-x-[22px] min-[360px]:p-6 nav:inset-x-8 nav:p-7',
          stage === 2 ? 'opacity-100' : OVERLAY_OFF,
        )}
      >
        <Eyebrow className="text-[10px] leading-[1.65]">What your report covers</Eyebrow>
        <Heading level={4} size="minor" className="mb-6 mt-[17px] text-[1.8125rem] leading-[1.12] min-[360px]:text-[2.0625rem] nav:text-[2.1875rem]">
          Follow the <br />
          local connection.
        </Heading>
        <dl className="m-0">
          {STORY_REPORT_ROWS.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 border-b border-dashed border-brand-rule py-[15px] text-[11px] min-[360px]:text-xs">
              <dt>{label}</dt>
              <dd className="m-0 text-[10px] font-medium min-[360px]:text-[11px]">{value}</dd>
            </div>
          ))}
        </dl>
        <span className="mt-3.5 block text-[10px] text-brand-muted">Sample structure. No campaign results shown.</span>
      </div>

      <div
        role="group"
        aria-label="Story chapters"
        className="absolute bottom-4 left-4 right-4 z-[5] flex justify-center gap-[5px]"
      >
        {STORY_STEPS.map((s, i) => (
          <button
            key={s.short}
            type="button"
            aria-pressed={stage === i ? 'true' : 'false'}
            aria-controls={STAGE_ID}
            onClick={() => onGo(i)}
            className={cn(
              'min-h-11 border px-[9px] py-[9px] font-label text-[11px] min-[360px]:px-[13px] nav:min-h-[38px] nav:px-3 nav:py-2',
              stage === i ? 'border-navy bg-navy text-brand-sheet' : 'border-brand-rule bg-brand-sheet text-navy',
            )}
          >
            <span className="sr-only">Show chapter </span>
            {String(i + 1).padStart(2, '0')}
            <span className="ml-[5px] min-[360px]:ml-[9px]">{s.short}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The product story. Every chapter's text is always in the page (the stage only
 * illustrates it). From 800px with motion allowed, the stage is sticky and follows the
 * chapter nearest the middle of the screen as the visitor scrolls (the reference's
 * followStory: nothing plays by itself); under reduced motion it sits beside the chapters
 * and changes only when a chapter button is pressed. Below 800px there is no stage: each
 * chapter is a card with its own photo (brief §2.5, no sticky or scrubbed behaviour). The buttons
 * carry aria-pressed + aria-controls; the stage cards that are not current are inert
 * and hidden from assistive technology.
 */
export function HomeStory() {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const reduced = useMediaQuery(REDUCED_MOTION_QUERY);
  const follow = desktop && !reduced;
  const [stage, setStage] = useState(0);
  const stageRef = useRef(null);
  const stepRefs = useRef([]);

  // The reference resets to the first chapter whenever the breakpoint or motion setting changes.
  useEffect(() => {
    setStage(0);
  }, [follow]);

  useEffect(() => {
    if (!follow) return undefined;
    let raf = 0;
    const pick = () => {
      raf = 0;
      const st = stageRef.current;
      if (!st) return;
      const box = st.getBoundingClientRect();
      if (!(box.bottom > 0 && box.top < window.innerHeight)) return;
      const center = window.innerHeight * 0.52;
      let best = 0;
      let distance = Infinity;
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - center);
        if (d < distance) {
          distance = d;
          best = i;
        }
      });
      setStage(best);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(pick);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [follow]);

  const go = useCallback((n) => {
    setStage(n);
    if (follow) {
      const step = stepRefs.current[n];
      if (step && typeof step.scrollIntoView === 'function') step.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const st = stageRef.current;
    if (st && typeof st.scrollIntoView === 'function') st.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [follow, reduced]);

  return (
    <SiteSection
      id="how-it-works"
      width="full"
      aria-labelledby="home-story-title"
      className="scroll-mt-[110px]"
      containerClassName={HOME_WIDTH}
    >
      <Reveal className="relative mb-[38px] nav:mb-[70px]">
        <Eyebrow>Good things are closer than you think</Eyebrow>
        <Heading level={2} size="feature" id="home-story-title" className="mb-6 mt-5 text-[2.875rem] leading-[1.06]">
          It starts with <br />
          a little curiosity.
        </Heading>
        {/* Beside the heading from 800px at the default text size; an em query, so with
            enlarged text (200%: from 1600px) it stays under the heading, never past the
            page's right edge; and never wider than the column (it wraps instead). */}
        <MarginNote
          hideOnMobile={false}
          className={cn(
            'relative mt-[25px] block w-max max-w-full text-[1.625rem] leading-[1.05] nav:text-[1.9375rem]',
            '[@media(min-width:50em)]:absolute [@media(min-width:50em)]:bottom-1 [@media(min-width:50em)]:left-[65%] [@media(min-width:50em)]:mt-0',
          )}
        >
          follow the coaster ↓
        </MarginNote>
      </Reveal>
      <div className="grid grid-cols-1 items-start nav:grid-cols-[.83fr_1.17fr] nav:gap-10 xl:gap-[75px]">
        <div>
          {STORY_STEPS.map((s, i) => (
            <article
              key={s.chapter}
              ref={(el) => { stepRefs.current[i] = el; }}
              aria-labelledby={`home-story-step-${i}`}
              data-current={stage === i ? 'true' : 'false'}
              className={cn(
                'border-t border-brand-rule py-8 nav:border-t-0 nav:py-[35px] nav:motion-safe:min-h-[490px] nav:motion-safe:py-20',
                i === 0 && 'nav:motion-safe:pt-[60px]',
                i === STORY_STEPS.length - 1 && 'nav:motion-safe:min-h-[470px]',
              )}
            >
              <SiteImage
                id={s.photo}
                ratio="1x1"
                box="6x5"
                sizes="100vw"
                className="mb-6 border border-brand-rule nav:hidden"
              />
              <Eyebrow index={i + 1} tone="rust">{s.chapter}</Eyebrow>
              <Heading
                level={3}
                size="card"
                id={`home-story-step-${i}`}
                className="my-4 text-[2.125rem] leading-[1.12] nav:my-[23px] nav:text-[2.375rem] xl:text-[2.75rem]"
              >
                {s.title[0]} <br />
                {s.title[1]}
              </Heading>
              <p className="mb-[23px] max-w-[32.5rem] text-base text-brand-muted nav:max-w-[20.9375rem]">{s.text}</p>
              {stepAction(i)}
            </article>
          ))}
        </div>
        <StoryStage stage={stage} onGo={go} stageRef={stageRef} />
      </div>
    </SiteSection>
  );
}

// ---------------------------------------------------------------------------
// Audience blocks
// ---------------------------------------------------------------------------
function EditorialList({ items, dark = false }) {
  return (
    <ul className="m-0 mb-[34px] max-w-[27.5rem] list-none p-0 text-sm">
      {items.map((text, i) => (
        <li
          key={text}
          className={cn(
            'flex items-baseline gap-[18px] border-b border-dashed py-4',
            dark ? 'border-brand-sheet/25' : 'border-brand-rule',
          )}
        >
          <span className={cn('font-label text-[11px]', dark ? 'text-brand-sheet/70' : 'text-brand-muted')}>
            {String(i + 1).padStart(2, '0')}
          </span>
          {text}
        </li>
      ))}
    </ul>
  );
}

export const BUSINESS_POINTS = [
  'One paid advertiser per category, per venue.',
  'Printed placement. A digital offer to match.',
  'Reporting on the actions people take.',
];

/**
 * HOME-BIZ-01 (a business owner with her coaster ad) with the illustrated example ad, smaller,
 * overlapping the photo's lower-left corner (brief §3.1 item 4). The ad is a decorative
 * print mock-up, named once for screen readers.
 */
function PrintedOffer() {
  return (
    <div className="relative mt-2.5 pb-[120px] pl-[18px] nav:mt-0 nav:pb-[90px] nav:pl-[70px]">
      <SiteImage
        id="HOME-BIZ-01"
        ratio="4x5"
        tone="navy"
        sizes="(min-width: 1808px) 749px, (min-width: 800px) 42vw, 90vw"
        className="border border-brand-sheet/20"
      />
      <MarginNote
        tone="sheet"
        tilt={9}
        hideOnMobile={false}
        className="absolute right-2 top-[-18px] z-[3] text-[1.5rem] leading-[1.05] [text-shadow:1px_1px_1px_theme(colors.navy.DEFAULT)] nav:right-[5px] nav:text-[1.75rem]"
      >
        a small space.
        <br />
        a proper introduction.
      </MarginNote>
      <div
        role="img"
        aria-label="An example of your ad space: a printed card that reads “Something good. Right here.”"
        className="absolute bottom-0 left-0 z-[2] w-[200px] -rotate-[7deg] bg-brand-paper px-4 pt-4 text-navy shadow-[8px_9px_0_theme(colors.navy-stronger/85%)] min-[360px]:w-[220px] nav:w-[230px] min-[1151px]:w-[250px]"
      >
        <div className="flex justify-between border-b border-navy pb-2.5 font-label text-[9px]">
          <span>LOCAL FAVORITES</span>
          <span>NO. 001</span>
        </div>
        <div className="my-5 font-display text-[2.125rem] font-medium leading-[1.04] tracking-[-0.03em] min-[1151px]:text-[2.5rem]">
          Something
          <br />
          good.
          <br />
          <i className="font-hand text-[1.15em] font-medium text-brand-rust">Right here.</i>
        </div>
        <div className="-mx-4 h-px border-t border-dashed border-navy" />
        <div className="py-3 text-center font-label text-[9px]">AN EXAMPLE OF YOUR AD SPACE</div>
      </div>
    </div>
  );
}

/** "Be the business they discover next." (#businesses): the inset navy block. */
export function BusinessesBlock() {
  return (
    <SiteSection
      id="businesses"
      tone="navy"
      inset
      width="full"
      spacing="none"
      aria-labelledby="home-businesses-title"
      className="scroll-mt-[110px] overflow-hidden pb-[42px] pt-[55px] nav:py-[75px] min-[1151px]:py-[95px]"
      containerClassName={cn(HOME_WIDTH, 'grid grid-cols-1 items-center gap-[30px] nav:grid-cols-2 nav:gap-[45px] min-[1151px]:gap-20')}
    >
      <Reveal>
        <Eyebrow>For local businesses</Eyebrow>
        <Heading
          level={2}
          size="feature"
          accent="orange"
          id="home-businesses-title"
          className="mb-6 mt-5 text-[2.5625rem] leading-[1.06] min-[360px]:text-[3.0625rem] nav:text-[clamp(2.75rem,5vw,4.375rem)]"
        >
          Be the business <br />
          they discover <br />
          <em>next.</em>
        </Heading>
        <p className="mb-[30px] max-w-[27.1875rem] text-base leading-[1.65] text-brand-lilac nav:text-[1.0625rem]">
          Your neighborhood is full of people who haven’t found you yet. Put your offer on the tables at the places
          they already love.
        </p>
        <EditorialList items={BUSINESS_POINTS} dark />
        <p className="-mt-3 mb-7 text-[0.9375rem] text-brand-sheet/90" data-testid="home-plans-from">
          <PlansFromLine />.
        </p>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <EnquiryButton role="advertiser" variant="inverse">Check availability</EnquiryButton>
          <TextLink to="/pricing" tone="inverse" arrow>See pricing</TextLink>
        </div>
        <span className="mt-3.5 block max-w-[25rem] text-sm text-brand-sheet/80">Subject to venue and category availability.</span>
      </Reveal>
      <PrintedOffer />
    </SiteSection>
  );
}

export const VENUE_POINTS = [
  'Your venue’s name and QR code.',
  'We handle printing and delivery.',
  'Put them out as you would any coaster.',
];

/** "Your next round of coasters is on us." (#venues). The copy comes first in the page; the picture sits left from 800px. */
export function VenuesBlock() {
  return (
    <SiteSection
      id="venues"
      width="full"
      aria-labelledby="home-venues-title"
      className="scroll-mt-[110px]"
      containerClassName={cn(HOME_WIDTH, 'grid grid-cols-1 items-center gap-[30px] nav:grid-cols-2 nav:gap-[55px] min-[1151px]:gap-[95px]')}
    >
      <Reveal>
        <Eyebrow>For bars &amp; restaurants</Eyebrow>
        <Heading
          level={2}
          size="feature"
          id="home-venues-title"
          className="mb-6 mt-5 text-[2.5625rem] leading-[1.06] min-[360px]:text-[3.0625rem] nav:text-[clamp(2.75rem,5vw,4.375rem)]"
        >
          Your next round <br />
          of coasters <br />
          is on us.
        </Heading>
        <p className="mb-[30px] max-w-[27.1875rem] text-base leading-[1.65] text-brand-muted nav:text-[1.0625rem]">
          Custom coasters for your venue, paid for by local advertisers. Useful for your tables. A little extra
          discovery for your guests.
        </p>
        <EditorialList items={VENUE_POINTS} />
        <Button to="/venue/apply">Get free coasters</Button>
        <span className="mt-3.5 block max-w-[25rem] text-sm text-brand-muted">We’ll confirm eligibility, quantities, and timing.</span>
        <EnquiryButton role="venue" variant="secondary" arrow={false} className={cn(DEMO_TEXT_LINK, 'mt-[18px]')}>
          Talk to the team<span aria-hidden="true">{' →'}</span>
        </EnquiryButton>
      </Reveal>
      <div className="relative mt-[15px] overflow-hidden bg-navy nav:order-first nav:mt-0">
        <SiteImage
          id="HOME-VEN-01"
          ratio="4x5"
          tone="navy"
          sizes="(min-width: 1808px) 778px, (min-width: 800px) 45vw, 100vw"
        />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-b from-navy/5 via-transparent to-navy/45" />
        <MarginNote
          tone="inherit"
          hideOnMobile={false}
          className="absolute left-[27px] top-[35px] z-[1] text-[2rem] leading-[1.05] text-brand-sheet [text-shadow:1px_1px_1px_theme(colors.navy.DEFAULT)]"
        >
          a little more local
          <br />
          with every round.
        </MarginNote>
        <div className="absolute bottom-[30px] left-5 right-[25px] z-[2] -rotate-[5deg] bg-brand-sheet p-[25px] text-navy shadow-[5px_5px_0_theme(colors.navy.DEFAULT/33%)] min-[360px]:left-7 min-[360px]:right-[35px] nav:bottom-10 nav:right-[55px]">
          <Eyebrow>The next round</Eyebrow>
          <strong className="my-[7px] block font-display text-[3.625rem] font-medium leading-[1.2] tracking-[-0.025em] min-[360px]:text-[4.0625rem]">
            On us.
          </strong>
          <span className="text-[13px]">Sponsored by local businesses.</span>
        </div>
      </div>
    </SiteSection>
  );
}

// ---------------------------------------------------------------------------
// "Know what went out. See what came back." (#reporting)
// ---------------------------------------------------------------------------
/** Report wording per the copy audit: placements are recorded by each venue (not per design). */
export const REPORT_COLUMNS = [
  { label: 'Placement', title: 'Coasters placed', text: 'Placement records from each venue.' },
  { label: 'Interest', title: 'Scans & claims', text: 'Recorded scans, page visits, and offer claims.' },
  { label: 'Action', title: 'Leads & redemptions', text: 'Submitted leads and recorded code redemptions.' },
];

export function ReportingBlock() {
  return (
    <SiteSection
      id="reporting"
      width="full"
      spacing="none"
      aria-labelledby="home-reporting-title"
      className="scroll-mt-[110px]"
      containerClassName={cn(HOME_WIDTH, 'border-t border-brand-rule py-[66px] nav:pb-[85px] nav:pt-[75px]')}
    >
      <div className="nav:grid nav:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] nav:items-center nav:gap-x-[60px]">
      <Reveal>
        <Eyebrow>A clearer picture</Eyebrow>
        <Heading
          level={2}
          size="feature"
          id="home-reporting-title"
          className="mb-6 mt-5 text-[2.6875rem] leading-[1.06] nav:text-[2.8125rem] min-[1151px]:text-[3.125rem]"
        >
          Know what went out. <br />
          See what came back.
        </Heading>
        <p className="mb-6 max-w-[31.25rem] text-brand-muted">
          Placement records from each venue show what went out. Recorded activity shows what people did next.
          Estimated exposure stays clearly separate.
        </p>
      </Reveal>
      <ReportPhoto className="mt-4 nav:mt-0" sizes="(min-width: 1808px) 922px, (min-width: 800px) 52vw, 100vw" />
      </div>
      <div className="mt-8 grid grid-cols-1 gap-[22px] nav:mt-[35px] nav:grid-cols-3 nav:gap-10">
        {REPORT_COLUMNS.map((c, i) => (
          <div key={c.title} className="border-t border-navy pt-5 nav:pt-[23px]">
            <Eyebrow index={i + 1} tone="rust" className="text-[11px] leading-[1.65]">{c.label}</Eyebrow>
            <Heading level={3} size="minor" className="my-3 text-[1.5625rem] leading-[1.12] nav:my-4 nav:text-2xl nav:leading-[1.12]">{c.title}</Heading>
            <p className="my-2.5 text-sm text-brand-muted nav:my-0 nav:max-w-[17.5rem]">{c.text}</p>
          </div>
        ))}
      </div>
      <p className="mb-0 mt-[26px] text-sm text-brand-muted">
        Reporting varies by plan. A scan or claim is not a guaranteed visit or sale.
      </p>
    </SiteSection>
  );
}

// ---------------------------------------------------------------------------
// "Small format. Clear commitment." (#pricing): heading, the pricing teaser, the free listing
// ---------------------------------------------------------------------------
export function PricingBlock() {
  // Upgrade brief §2.8: while no venue takes free listings, say so (never promise three venues).
  const freeListing = useFreeListingState();
  return (
    <div className="bg-brand-paper">
      <SiteSection
        id="pricing"
        width="full"
        spacing="none"
        aria-labelledby="home-pricing-title"
        className="scroll-mt-[110px] border-y border-navy/15 bg-brand-rule/50 py-[65px] nav:py-[90px]"
        containerClassName={HOME_WIDTH}
      >
        <Reveal className="mb-[30px] nav:mb-11 nav:text-center">
          <Eyebrow>Your place on the coasters</Eyebrow>
          <Heading level={2} size="feature" id="home-pricing-title" className="mb-6 mt-5">
            Small format. <br />
            Clear commitment.
          </Heading>
          <p className="m-0 text-[1.0625rem] text-brand-muted">Choose your placement. Know exactly what’s included.</p>
        </Reveal>
        {/* Upgrade brief §3.1 item 7: a compact teaser; the calculator lives on /pricing only. */}
        <PricingTeaser photos="thumb" />
        <div className="mt-6">
          <Button to="/pricing" variant="secondary">See pricing</Button>
        </div>
        <div className="mt-7 flex flex-col items-start gap-6 border-y border-navy/25 py-7 nav:mt-[42px] nav:flex-row nav:items-center nav:justify-between nav:gap-[35px]">
          <div>
            <Eyebrow>Start with a hello</Eyebrow>
            <Heading level={3} size="minor" className="my-[11px] text-[1.6875rem] leading-[1.12] nav:text-[1.5625rem]">
              A free listing is a good first step.
            </Heading>
            <p className="m-0 text-sm text-brand-muted" data-free-listing-note="" data-state={freeListing}>
              {freeListing === 'closed' ? FREE_LISTING_CLOSED_NOTE : 'One offer. No coaster placement. No card required.'}
            </p>
          </div>
          <Button to="/free-listing" variant="secondary" className="shrink-0">Start with a free listing</Button>
        </div>
      </SiteSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The founder note and the FAQ
// ---------------------------------------------------------------------------
export function FounderNote() {
  return (
    <SiteSection
      width="full"
      spacing="none"
      aria-labelledby="home-founder-title"
      className="py-[70px] nav:py-[120px]"
      containerClassName={cn(HOME_WIDTH, 'grid grid-cols-1 items-start gap-[30px] nav:grid-cols-[.55fr_1.45fr] nav:gap-[45px] min-[1151px]:gap-[70px]')}
    >
      <Eyebrow as="p" className="m-0 leading-[1.8] nav:max-w-[170px] nav:pt-5">A note from the neighborhood</Eyebrow>
      <Reveal className="relative max-w-[48.75rem]">
        <MarginNote as="p" tone="inherit" tilt={0} decorative={false} hideOnMobile={false} className="m-0 text-[1.5625rem] leading-[1.05] nav:text-[1.75rem]">
          To the places that make a place,
        </MarginNote>
        <Heading
          level={2}
          size="feature"
          accent="rust"
          id="home-founder-title"
          className="mb-6 mt-5 text-[2.5rem] leading-[1.06] min-[360px]:text-[2.9375rem] nav:text-[3.5rem] min-[1151px]:text-[4rem]"
        >
          Local businesses <br />
          make a neighborhood. <br />
          Let’s help people <br />
          <em>find them.</em>
        </Heading>
        <p className="my-4 max-w-[32.5rem] text-base text-brand-muted nav:text-[1.0625rem]">
          Tappd started with a simple idea at a bar in South Jersey: that little coaster could do more for the
          community around it.
        </p>
        <p className="my-4 max-w-[32.5rem] text-base text-brand-muted nav:text-[1.0625rem]">
          Built by Rauf, a local founder with a background in hospitality marketing. Growing one neighborhood at a
          time.
        </p>
        <TextLink to="/our-story" arrow>Read the Tappd story</TextLink>
        <MarginNote as="p" tilt={-6} decorative={false} hideOnMobile={false} className="mb-0 mt-[35px] block w-40 max-w-full text-[1.5625rem] leading-[1.05] nav:text-[1.75rem]">
          See you around,
          <br />
          Rauf
        </MarginNote>
      </Reveal>
    </SiteSection>
  );
}

/**
 * "A few good questions." (#faqs). The heading and the list share one grid parent (the
 * list spans the right column from 800px), so the questions sit under the heading's own
 * parent, as the FAQ checks expect.
 */
export function HomeFaq({ items }) {
  return (
    <SiteSection
      id="faqs"
      width="full"
      spacing="none"
      aria-labelledby="home-faq-title"
      className="scroll-mt-[110px]"
      containerClassName={cn(
        HOME_WIDTH,
        'grid grid-cols-1 border-t border-brand-rule py-[66px] nav:grid-cols-[.85fr_1.15fr] nav:grid-rows-[auto_auto_auto_1fr] nav:gap-x-[55px] nav:pb-[104px] nav:pt-20 min-[1151px]:gap-x-[90px]',
      )}
    >
      <Eyebrow className="nav:col-start-1">Before you pull up a chair</Eyebrow>
      <Heading
        level={2}
        size="feature"
        id="home-faq-title"
        className="mb-6 mt-5 text-[2.875rem] leading-[1.06] nav:col-start-1 nav:text-[3.5rem]"
      >
        A few good <br />
        questions.
      </Heading>
      <TextLink href="mailto:hello@tappd.app" arrow className="justify-self-start nav:col-start-1">Ask us anything</TextLink>
      <FaqList id="home-faq" items={items} jsonLd className="mt-[25px] nav:col-start-2 nav:row-span-4 nav:row-start-1 nav:mt-0" />
    </SiteSection>
  );
}
